import { ROTATION_VECTOR, SOLVED_TOP_PIECES, SOLVED_BOTTOM_PIECES, DEFAULT_SQ1_SCHEME, } from "./constants";
import { Square1Builder } from "./interface";
import { DEG_30_RADIANS } from "./../../math/constants";
import { Geometry } from "./../../geometry/geometry";
import { Face } from "./../../geometry/face";
import { Group } from "../../geometry/group";
import { Vector3 } from "../../math/vector";
const INNER_FACE_COLOR = { value: "#333", stroke: "#333" };
export class Square1 extends Square1Builder {
    constructor(topLayer = SOLVED_TOP_PIECES, bottomLayer = SOLVED_BOTTOM_PIECES, middleRotated = false, scheme = DEFAULT_SQ1_SCHEME, sideLength = 1.25) {
        super(topLayer, bottomLayer, middleRotated, scheme, sideLength);
    }
    square1Corner(top, side1, side2) {
        const points = [
            // Top
            Vector3.fromValues(0, 0, this.halfSide),
            Vector3.fromValues(this.halfSide, this.halfEdgePiece, this.halfSide),
            Vector3.fromValues(this.halfSide, this.halfSide, this.halfSide),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, this.halfSide),
            // Bottom
            Vector3.fromValues(0, 0, this.halfSide - this.layerWidth),
            Vector3.fromValues(this.halfSide, this.halfEdgePiece, this.halfSide - this.layerWidth),
            Vector3.fromValues(this.halfSide, this.halfSide, this.halfSide - this.layerWidth),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, this.halfSide - this.layerWidth),
        ];
        const faces = [
            new Face([0, 1, 2, 3], points, top),
            // TODO: the faces commented out here are the underside of the pieces so
            // they show gray when the cube is scrambled. But they are overlapping sometimes
            // with outward sticker faces. removing them for now, but it'd be nice to
            // fix this.
            new Face([4, 5, 6, 7], points, INNER_FACE_COLOR),
            new Face([0, 1, 5, 4], points, INNER_FACE_COLOR),
            new Face([2, 3, 7, 6], points, side1),
            new Face([1, 2, 6, 5], points, side2),
            new Face([0, 3, 7, 4], points, INNER_FACE_COLOR),
        ];
        const innerCentroid = Vector3.fromValues(this.halfSide / 2, this.halfSide / 2, this.halfSide / 2);
        faces[1].centroid = innerCentroid;
        faces[2].centroid = innerCentroid;
        faces[5].centroid = innerCentroid;
        return new Geometry(points, faces);
    }
    square1Edge(top, side) {
        const points = [
            // Top
            Vector3.fromValues(0, 0, this.halfSide).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, this.halfSide).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
            Vector3.fromValues(-this.halfEdgePiece, this.halfSide, this.halfSide).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
            // Bottom
            Vector3.fromValues(0, 0, this.halfSide - this.layerWidth).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, this.halfSide - this.layerWidth).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
            Vector3.fromValues(-this.halfEdgePiece, this.halfSide, this.halfSide - this.layerWidth).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS),
        ];
        const faces = [
            new Face([0, 1, 2], points, top),
            new Face([3, 4, 5], points, INNER_FACE_COLOR),
            new Face([1, 2, 5, 4], points, side),
            new Face([0, 1, 4, 3], points, INNER_FACE_COLOR),
            new Face([0, 2, 5, 3], points, INNER_FACE_COLOR),
        ];
        const innerFaceCentroid = Vector3.fromValues(0, this.halfSide / 2, this.halfSide / 2).rotateZ(Vector3.fromValues(0, 0, 0), DEG_30_RADIANS);
        // Override centroid to avoid drawing over outside stickers
        faces[1].centroid = innerFaceCentroid;
        faces[3].centroid = innerFaceCentroid;
        faces[4].centroid = innerFaceCentroid;
        return new Geometry(points, faces);
    }
    square1Middle(front, side, back) {
        const vertices = [
            Vector3.fromValues(-this.halfSide, -this.halfSide, this.halfMiddleWidth),
            Vector3.fromValues(-this.halfSide, this.halfSide, this.halfMiddleWidth),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, this.halfMiddleWidth),
            Vector3.fromValues(-this.halfEdgePiece, -this.halfSide, this.halfMiddleWidth),
            Vector3.fromValues(-this.halfSide, -this.halfSide, -this.halfMiddleWidth),
            Vector3.fromValues(-this.halfSide, this.halfSide, -this.halfMiddleWidth),
            Vector3.fromValues(this.halfEdgePiece, this.halfSide, -this.halfMiddleWidth),
            Vector3.fromValues(-this.halfEdgePiece, -this.halfSide, -this.halfMiddleWidth),
        ];
        const faces = [
            new Face([0, 1, 2, 3], vertices, { value: "#333" }),
            new Face([4, 5, 6, 7], vertices, { value: "#333" }),
            new Face([0, 1, 5, 4], vertices, side),
            new Face([1, 2, 6, 5], vertices, back),
            // new Face([2, 3, 7, 6], vertices, { value: "#333" }),
            new Face([0, 3, 7, 4], vertices, front),
        ];
        const innerFaceCentroid = Vector3.fromValues(-this.halfSide / 2, 0, 0);
        // Override centroid to avoid drawing over outside stickers
        faces[0].centroid = innerFaceCentroid;
        faces[1].centroid = innerFaceCentroid;
        faces[2].centroid = Vector3.fromValues(-(this.halfSide + this.halfSide * 0.45), 0, 0);
        return new Geometry(vertices, faces);
    }
    buildSquare1(top, bottom, middleRotated) {
        const topLayer = new Group(this.makeLayer(top));
        const bottomLayer = new Group(this.makeLayer(bottom));
        bottomLayer.rotate(Math.PI, 1, 0, 0);
        bottomLayer.rotate(DEG_30_RADIANS, 0, 0, 1);
        const pieces = [topLayer, bottomLayer];
        const frontColor = this.scheme.front || DEFAULT_SQ1_SCHEME.front;
        const leftColor = this.scheme.left || DEFAULT_SQ1_SCHEME.left;
        const backColor = this.scheme.back || DEFAULT_SQ1_SCHEME.back;
        const rightColor = this.scheme.right || DEFAULT_SQ1_SCHEME.right;
        const m1 = this.square1Middle(frontColor, leftColor, backColor);
        const m2 = this.square1Middle(backColor, rightColor, frontColor);
        m2.rotate(Math.PI, 0, 0, 1);
        if (middleRotated) {
            m2.rotate(Math.PI, ROTATION_VECTOR.x, ROTATION_VECTOR.y, ROTATION_VECTOR.z);
        }
        pieces.push(m1);
        pieces.push(m2);
        this.faces = {
            top: topLayer,
            bottom: bottomLayer,
        };
        return pieces;
    }
}
